import React from 'react';
import { GetStaticProps } from 'next';
import * as Sentry from '@sentry/nextjs';
import _omit from 'lodash/omit';

import styles from './styles.module.scss';

import { withLayoutAndSession } from '@utils/hocs';
import { PageWithProvidersAndLayout } from '@utils/types/nextApp';
import MetaHead from '@components/_shared/metaHead';
import BuyRovePropertyHero from '@components/landing/buyRoveProperty/hero';
import BuyRovePropertyContact from '@components/landing/buyRoveProperty/contact';
import {
  TBuyRovePropertyLanding,
  LandingSuccessStoriesType,
} from '@utils/types/landings';
import { getBuyRovePropertyLanding } from '@api/landings/getBuyRovePropertyLanding';
import SuccessStories from '@components/landing/_shared/successStories';
import HowItWorks from '@components/landing/_shared/howItWorks';
import { searchListingsRequest } from '@api/listings/searchListingsRequest';
import { FeaturedListingType } from '@utils/types/featuredListing';

type Props = {
  buyRovePropertyLanding: TBuyRovePropertyLanding;
  successStories: LandingSuccessStoriesType;
  listings: FeaturedListingType[];
};

const BuyRoveProperty: PageWithProvidersAndLayout<Props> = (props) => {
  const {
    buyRovePropertyLanding: {
      buyRovePropertyHero,
      buyRovePropertyProperties,
      howItWorks,
      contact,
      seoFields,
    },
    successStories,
    listings,
  } = props;

  return (
    <>
      <MetaHead
        title={seoFields?.title || buyRovePropertyHero.headline}
        description={seoFields?.description || buyRovePropertyHero.subheadline}
        ogImage={seoFields?.ogImage}
      />

      <div className={styles.page}>
        <BuyRovePropertyHero
          {...buyRovePropertyHero}
          properties={buyRovePropertyProperties}
          listings={listings}
        />

        <hr className={styles.divider} />

        <SuccessStories {...successStories} />

        <hr className={styles.divider} />

        <HowItWorks {...howItWorks} />

        <hr className={styles.divider} />

        <BuyRovePropertyContact {...contact} />
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps<any> = async (context) => {
  try {
    const landingData = await getBuyRovePropertyLanding();

    const guestyIds =
      landingData.buyRovePropertyLanding.buyRovePropertyProperties?.properties
        ?.filter((el) => !!el.listing?.guestyId)
        .map((el) => el.listing.guestyId as string) || [];

    const { listings } = await searchListingsRequest({ guestyIds });

    return {
      props: {
        ...landingData,
        listings: listings.map((listing) =>
          _omit(listing, [
            'available_dates',
            'calendar_nightly_price',
            'valid_start_dates',
            'valid_end_dates',
            'calendar_min_nights',
          ])
        ),
      },
      revalidate: 10,
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: 10,
    };
  }
};

export default withLayoutAndSession(BuyRoveProperty, {
  headerProps: { theme: 'dark' },
  footerProps: { theme: 'dark' },
});
